import axios, { AxiosError } from "axios";

const API_URL = `${import.meta.env.VITE_API_URL}/variants`;

// Helper to extract error message from backend response
const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError && error.response?.data) {
    const data = error.response.data;
    return data.message || data.error || error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred";
};

// Types
export interface VariantProduct {
  _id: string;
  name: string;
  bunch: number;
  image: string;
  colorCode: string;
  colorName: string;
}

export interface VariantCategory {
  _id: string;
  name: string;
}

export interface VariantBranch {
  _id: string;
  name: string;
}

export interface Variant {
  _id: string;
  s01: string;
  s02: string;
  s03: string;
  farm: string;
  country: string;
  stock: number;
  price: number;
  isPrivate: boolean;
  privateDate: string | null;
  product: VariantProduct;
  category: VariantCategory;
  branch: VariantBranch;
  extra: {
    productName: string;
    colorName: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ColorFilter {
  name: string;
  code: string | null;
}

export interface FilterGroups {
  colors: ColorFilter[];
  country: string[];
  farm: string[];
  s01: string[];
  s02: string[];
  s03: string[];
}

export interface AvailableCategory {
  _id: string;
  name: string;
}

export interface VariantsResponse {
  variants: Variant[];
  filterGroups?: FilterGroups;
  availableCategories?: AvailableCategory[];
  page: number;
  pages: number;
  sortBy: string;
  filter: Record<string, unknown>;
}

export interface FetchVariantsOptions {
  branch: string;
  page?: number;
  category?: string | null;
  colors?: string[];
  s01?: string[];
  s02?: string[];
  s03?: string[];
  farm?: string[];
  country?: string[];
  search?: string;
  sortBy?: string;
}

export interface CreateVariantData {
  product: string;
  s01: string;
  s02: string;
  s03: string | Date;
  farm: string;
  country: string;
  stock: number;
  price: number;
  isPrivate?: boolean;
  privateDate?: string | Date | null;
  privateTime?: string | null;
}

export interface UpdateVariantData extends CreateVariantData {
  _id: string;
}

export interface BulkVariantItem {
  product: string;
  category: string;
  branch: string;
  s01: string;
  s02: string;
  s03: string | Date | null;
  farm: string;
  country: string;
  stock: number;
  price: number;
  isPrivate?: boolean;
  privateDate?: string | Date | null;
  privateTime?: string;
}

// API Functions

/**
 * Fetch variants with pagination and filters
 */
export const fetchVariants = async (
  options: FetchVariantsOptions
): Promise<VariantsResponse> => {
  const params = new URLSearchParams();

  if (options.branch) params.append("branch", options.branch);
  if (options.page) params.append("page", String(options.page));
  if (options.category) params.append("category", options.category);
  if (options.colors && options.colors.length > 0) params.append("colors", options.colors.join(","));
  if (options.s01 && options.s01.length > 0) params.append("s01", options.s01.join(","));
  if (options.s02 && options.s02.length > 0) params.append("s02", options.s02.join(","));
  if (options.s03 && options.s03.length > 0) params.append("s03", options.s03.join(","));
  if (options.farm && options.farm.length > 0) params.append("farm", options.farm.join(","));
  if (options.country && options.country.length > 0) params.append("country", options.country.join(","));
  if (options.search) params.append("search", options.search);
  if (options.sortBy) params.append("sortBy", options.sortBy);

  const { data } = await axios.get<VariantsResponse>(
    `${API_URL}?${params.toString()}`
  );
  return data;
};

/**
 * Fetch a single variant by ID
 */
export const fetchVariant = async (
  id: string
): Promise<{ success: boolean; data: Variant }> => {
  const { data } = await axios.get(`${API_URL}/${id}`);
  return data;
};

/**
 * Create a new variant
 */
export const createVariant = async (
  variantData: CreateVariantData
): Promise<{ message: string }> => {
  try {
    const { data } = await axios.post(API_URL, variantData);
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

/**
 * Bulk create variants
 */
export const createVariantsBulk = async (
  items: BulkVariantItem[]
): Promise<{ message: string }> => {
  try {
    const { data } = await axios.post(`${API_URL}/bulk`, { items });
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

/**
 * Update a variant
 */
export const updateVariant = async (
  variantData: UpdateVariantData
): Promise<{ success: boolean; message: string }> => {
  try {
    const { data } = await axios.put(API_URL, variantData);
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

/**
 * Delete a variant
 */
export const deleteVariant = async (
  id: string
): Promise<{ message: string }> => {
  try {
    const { data } = await axios.delete(`${API_URL}/${id}`);
    return data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};
